import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, createBlock as _createBlock, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "container my-12" }
const _hoisted_2 = { key: 0 }
const _hoisted_3 = {
  key: 0,
  class: "mt-8 space-y-6 clinical-blocks"
}

import { WebTemplate } from '@bigpicturemedical/openehr-vue';
import { blockMap, PastPresentingComplaint } from '@bigpicturemedical/clinical-blocks';
import { MedKitButton, MedKitSelect } from '@bigpicturemedical/medkit';
import { ref, onMounted, PropType } from 'vue';
import apiClient from '@/services/api/client/apiClient';
import { Patient } from '@/models';
import { isFeatureFlagEnabled } from '@/helpers/feature-flag.helper';
import { FEATURES } from '@/constants';
import { useRoute, useRouter } from 'vue-router';
import { useNotificationStore } from '@/stores/notification.store';
import { useI18n } from 'vue-i18n';
import { useSessionStore } from '@/stores/session.store';


export default /*#__PURE__*/_defineComponent({
  props: {
  patient: {
    type: Object as PropType<Patient>,
    required: true
  }
},
  setup(__props) {

const props = __props



const availableTemplates: {
  label: string;
  value: string;
}[] = [
  {
    label: 'Kitchen Sink',
    value: 'presenting_complaint_full.v0'
  },
  {
    label: 'Chest Pain',
    value: 'presenting_complaint_chest_pain.v0'
  },
  {
    label: 'Breast Lump',
    value: 'presenting_complaint_breast_lump.v0'
  },
  {
    label: 'Breast Pain',
    value: 'presenting_complaint_breast_pain.v0'
  },
  {
    label: 'Hearing Loss',
    value: 'presenting_complaint_hearing_loss.v0'
  }
];

const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const sessionStore = useSessionStore();
const notificationStore = useNotificationStore();

const data = ref(null);
const language = ref('en');
const template = ref(null);
const webTemplate = ref(null);
const idempotencyKey = ref(Date.now());
const loading = ref<boolean>(true);
const selectedTemplate = ref<string>(route.query.template || 'presenting_complaint_full.v0');
const pastPresentingComplaints = ref(null);

onMounted(async () => {
  if (!isFeatureFlagEnabled(FEATURES.CLINICAL_BLOCKS)) {
    return await router.push(`/patients/${props.patient.id}/details`);
  }

  await Promise.all([
    loadTemplate(),
    loadPastPresentingComplaints()
  ]);

  loading.value = false;
});

const loadTemplate = async () => {
  const response = await apiClient.get(`/v1/patients/${props.patient.id}/blocks/presenting-complaint/template`, {
    params: {
      template: selectedTemplate.value
    }
  });

  template.value = response.data.template;
};

const loadPastPresentingComplaints = async () => {
  const response = await apiClient.get(`/v1/patients/${props.patient.id}/blocks/presenting-complaint`, {
    params: {
      template: selectedTemplate.value
    }
  });

  pastPresentingComplaints.value = response.data;
};

const submit = async () => {
  if (await webTemplate.value.validate() === false) {
    notificationStore.snackbar = {
      autoDismiss: false,
      label: t('projects.clinical-blocks.composition-failed-validation'),
      color: 'danger',
      icon: 'warning'
    };
    return;
  }

  try {
    await apiClient.post(
      `/v1/patients/${props.patient.id}/blocks/presenting-complaint`,
      { template: selectedTemplate.value, ...data.value }
    );

    notificationStore.snackbar = {
      label: t('projects.clinical-blocks.composition-saved'),
      autoDismiss: true,
      dismissAfter: 6000,
      color: 'success'
    };

    data.value = null;
    idempotencyKey.value = Date.now();

    await loadPastPresentingComplaints();
  } catch (error) {
    if (error.response.status === 422) {
      webTemplate.value.setErrors(error.response.data.errors);

      notificationStore.snackbar = {
        autoDismiss: false,
        label: t('projects.clinical-blocks.composition-failed-validation'),
        color: 'danger',
        icon: 'warning'
      };

      return;
    }

    notificationStore.snackbar = {
      autoDismiss: false,
      label: t('projects.clinical-blocks.composition-failed-to-save'),
      color: 'danger',
      icon: 'warning'
    };

    console.error(error);
  }
};

const setSelectedTemplate = async (template: string) => {
  selectedTemplate.value = template;

  loading.value = true;

  await Promise.all([
    loadTemplate(),
    loadPastPresentingComplaints()
  ]);

  router.replace({ query: { ...route.query, template } });

  data.value = null;

  loading.value = false;
};


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("section", _hoisted_1, [
    (!template.value)
      ? (_openBlock(), _createElementBlock("p", _hoisted_2, " Loading... "))
      : _createCommentVNode("", true),
    (!loading.value)
      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
          _createVNode(_unref(MedKitSelect), {
            class: "mb-8",
            label: "Presenting Complaint Template",
            "model-value": selectedTemplate.value,
            options: availableTemplates,
            required: "",
            "onUpdate:modelValue": setSelectedTemplate
          }, null, 8, ["model-value"]),
          (_openBlock(), _createBlock(_unref(WebTemplate), {
            key: idempotencyKey.value,
            ref_key: "webTemplate",
            ref: webTemplate,
            modelValue: data.value,
            "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((data).value = $event)),
            template: template.value,
            "block-map": _unref(blockMap),
            language: language.value,
            territory: "AU",
            composer: { name: `${_unref(sessionStore).currentUser.given_name} ${_unref(sessionStore).currentUser.family_name}`, id: _unref(sessionStore).currentUser.id, id_scheme: 'UUID', id_namespace: 'development-bigpicturemedical-com' },
            "context-setting-code": "228"
          }, null, 8, ["modelValue", "template", "block-map", "language", "composer"])),
          _createVNode(_unref(MedKitButton), {
            class: "mt-6",
            label: "Submit",
            onClick: submit
          }),
          (pastPresentingComplaints.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(pastPresentingComplaints.value, (presentingComplaint) => {
                  return (_openBlock(), _createElementBlock(_Fragment, {
                    key: presentingComplaint.uid.value
                  }, [
                    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(presentingComplaint.content[0].data.events, (complaint, index) => {
                      return (_openBlock(), _createBlock(_unref(PastPresentingComplaint), {
                        key: index,
                        "presenting-complaint": complaint
                      }, null, 8, ["presenting-complaint"]))
                    }), 128))
                  ], 64))
                }), 128))
              ]))
            : _createCommentVNode("", true)
        ], 64))
      : _createCommentVNode("", true)
  ]))
}
}

})